import { Button, Col, Flex, Form, Input, Row } from "antd";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import LoaderContainer from "~Common/Page/LoaderContainer";
import PermissionCheck from "~Common/PermissionCheck";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useFetchLeaderCriteria } from "~pages/LeaderCriteria/hooks/useFetchLeaderCriteria.ts";
import { useUpdateLeaderCriteria } from "~pages/LeaderCriteria/hooks/useUpdateLeaderCriteria.ts";
import { useCreateLeaderCriteria } from "~pages/LeaderCriteria/LeaderCriteriaEditPage/hooks/useCreateLeaderCriteria.ts";
import type { LeaderCriteriaModel } from "~pages/LeaderCriteria/types/leaderCriteriaModel.ts";
import { noLeadingOrTrailingSpacesRule } from "~utils/noLeadingOrTrailingSpacesRule.ts";

const LeaderCriteriaEditPage = () => {
  const { customerId, sport, organizationId, leaderCriteriaId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [form] = Form.useForm<LeaderCriteriaModel>();

  const { data: leaderCriteria, isLoading } = useFetchLeaderCriteria(leaderCriteriaId);
  const { mutateAsync: createLeaderCriteria, isPending: isCreatePending } = useCreateLeaderCriteria();
  const { mutateAsync: updateLeaderCriteria, isPending: isUpdatePending } = useUpdateLeaderCriteria();

  const listUrl = `/${customerId}/admin/${sport}/${organizationId}/leaders-qualification`;

  useEffect(() => {
    if (leaderCriteria) {
      form.setFieldsValue({ name: leaderCriteria.name });
    }
  }, [leaderCriteria, form]);

  const pageTitle = leaderCriteriaId
    ? `${formatMessage({ id: "manage" })} - ${leaderCriteria?.name ?? formatMessage({ id: "loading" })}`
    : formatMessage({ id: "leaders.qualification.create" });

  const handleSubmit = async () => {
    const { name } = await form.validateFields();

    if (leaderCriteriaId) {
      await updateLeaderCriteria({ leaderCriteriaId, name });
      return;
    }

    await createLeaderCriteria({ name });
    navigate(listUrl, { state: { search: name } });
  };

  return (
    <Page title={pageTitle} tabTitle={pageTitle}>
      <LoaderContainer loading={isLoading}>
        <Row>
          <Col span={12}>
            <Flex vertical gap="middle">
              <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Form.Item
                  name="name"
                  label={formatMessage({ id: "name" })}
                  tooltip={formatMessage({ id: "form.help.leaders.qualification.name" })}
                  style={formItemSharedStyles}
                  rules={[
                    {
                      required: true,
                      message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "name" }) }),
                    },
                    {
                      min: 2,
                      message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
                    },
                    noLeadingOrTrailingSpacesRule(formatMessage),
                  ]}
                >
                  <Input />
                </Form.Item>
              </Form>
              <Flex justify="flex-end" gap="large">
                <PermissionCheck
                  permissionKeys={[PermissionNames.LEADER_QUALIFIER]}
                  permissionOptions={[leaderCriteriaId ? PermissionOperations.UPDATE : PermissionOperations.CREATE]}
                >
                  <Button type="primary" onClick={() => void handleSubmit()} disabled={isCreatePending || isUpdatePending}>
                    {formatMessage({ id: leaderCriteriaId ? "leaders.qualification.update" : "leaders.qualification.create" })}
                  </Button>
                </PermissionCheck>
                <Link to={listUrl}>
                  <Button type="primary">{formatMessage({ id: "close" })}</Button>
                </Link>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </LoaderContainer>
    </Page>
  );
};

export default LeaderCriteriaEditPage;
